import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Lightbulb,
  Lock,
  Plus,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { supabase } from '../lib/supabaseClient';
import { useUserAccess } from '../hooks/useUserAccess';
import BudgetSetupWizard from '../components/BudgetSetupWizard';
import CategoryIconBadge from '../components/CategoryIconBadge';
import { AnimatedCard } from '../components/animations/AnimatedCard';
import { AnimatedNumber } from '../components/animations/AnimatedNumber';
import { AnimatedProgressBar } from '../components/animations/AnimatedProgressBar';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import {
  BudgetConsommation,
  getBudgetProgressClass,
  getBudgetProgressStatus,
  getBudgetsConsommation,
  monthNameFromIndex,
  monthToDate,
  upsertBudget,
} from '../lib/services/budgetsService';

const rowVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: { delay: Math.min(i, 20) * 0.03, duration: 0.2 },
  }),
};

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const SHOW_EMPTY_KEY = 'budgets_show_empty';
const SORT_KEY = 'budgets_sort';

type SortColumn = 'categorie' | 'budget' | 'depense' | 'restant' | 'progression';
type SortDirection = 'asc' | 'desc';

interface SortState {
  column: SortColumn | null;
  direction: SortDirection | null;
}

function formatEuro(amount: number) {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function loadShowEmpty(): boolean {
  try {
    return localStorage.getItem(SHOW_EMPTY_KEY) === '1';
  } catch {
    return false;
  }
}

function loadSort(): SortState {
  try {
    const raw = localStorage.getItem(SORT_KEY);
    if (!raw) return { column: null, direction: null };
    const parsed = JSON.parse(raw) as { column?: string; direction?: string };
    const cols: SortColumn[] = ['categorie', 'budget', 'depense', 'restant', 'progression'];
    if (
      parsed.column &&
      cols.includes(parsed.column as SortColumn) &&
      (parsed.direction === 'asc' || parsed.direction === 'desc')
    ) {
      return { column: parsed.column as SortColumn, direction: parsed.direction };
    }
  } catch {
    /* ignore */
  }
  return { column: null, direction: null };
}

function restantOf(item: BudgetConsommation): number {
  return item.montant_alloue - item.montant_depense;
}

function isEmptyCategory(item: BudgetConsommation): boolean {
  // Masqué par défaut : dépensé = 0 ET pas de budget défini
  return item.montant_depense <= 0 && item.montant_alloue <= 0;
}

const Budgets: React.FC = () => {
  const { user } = useAuth();
  const { impersonatedUserId } = useImpersonation();
  const { hasFeature, loading: accessLoading } = useUserAccess();
  const budgetsEnabled = hasFeature('budgets');
  const prefersReducedMotion = usePrefersReducedMotion();

  const now = new Date();
  const [monthIndex, setMonthIndex] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [items, setItems] = useState<BudgetConsommation[]>([]);
  const [prevBudgetTotal, setPrevBudgetTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editItem, setEditItem] = useState<BudgetConsommation | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [showEmpty, setShowEmpty] = useState(loadShowEmpty);
  const [sort, setSort] = useState<SortState>(loadSort);

  const moisDate = useMemo(() => monthToDate(monthIndex, year), [monthIndex, year]);

  const prevMoisDate = useMemo(() => {
    let mi = monthIndex - 1;
    let y = year;
    if (mi < 0) {
      mi = 11;
      y -= 1;
    }
    return monthToDate(mi, y);
  }, [monthIndex, year]);

  const loadBudgets = useCallback(async () => {
    if (!budgetsEnabled) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [curr, prev] = await Promise.all([
        getBudgetsConsommation(moisDate),
        getBudgetsConsommation(prevMoisDate),
      ]);
      if (curr.error) {
        setError('Erreur lors du chargement des budgets');
        return;
      }
      setItems(curr.data ?? []);
      const prevTotal = (prev.data ?? [])
        .filter((i) => i.montant_alloue > 0)
        .reduce((s, i) => s + i.montant_alloue, 0);
      setPrevBudgetTotal(prevTotal);
    } catch (err) {
      console.error('[Budgets] loadBudgets:', err);
      setError('Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  }, [budgetsEnabled, moisDate, prevMoisDate, impersonatedUserId]);

  useEffect(() => {
    loadBudgets();
  }, [loadBudgets]);

  useEffect(() => {
    if (!user?.id || !budgetsEnabled) return;

    const channel = supabase
      .channel(`budgets-web-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'budgets', filter: `user_id=eq.${user.id}` },
        () => loadBudgets(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'transactions', filter: `user_id=eq.${user.id}` },
        () => loadBudgets(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, budgetsEnabled, loadBudgets]);

  const shiftMonth = (delta: number) => {
    let newIndex = monthIndex + delta;
    let newYear = year;
    if (newIndex < 0) {
      newIndex = 11;
      newYear -= 1;
    } else if (newIndex > 11) {
      newIndex = 0;
      newYear += 1;
    }
    setMonthIndex(newIndex);
    setYear(newYear);
  };

  const activeItems = useMemo(
    () => items.filter((i) => i.montant_alloue > 0),
    [items],
  );

  const toggleShowEmpty = () => {
    setShowEmpty((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SHOW_EMPTY_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const cycleSort = (column: SortColumn) => {
    setSort((prev) => {
      let next: SortState;
      if (prev.column !== column) {
        next = { column, direction: 'asc' };
      } else if (prev.direction === 'asc') {
        next = { column, direction: 'desc' };
      } else {
        next = { column: null, direction: null };
      }
      try {
        if (next.column && next.direction) {
          localStorage.setItem(SORT_KEY, JSON.stringify(next));
        } else {
          localStorage.removeItem(SORT_KEY);
        }
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const displayedItems = useMemo(() => {
    const list = showEmpty ? [...items] : items.filter((i) => !isEmptyCategory(i));

    if (!sort.column || !sort.direction) {
      return list.sort((a, b) =>
        a.categorie_nom.localeCompare(b.categorie_nom, 'fr', { sensitivity: 'base' }),
      );
    }

    const dir = sort.direction === 'asc' ? 1 : -1;
    const col = sort.column;

    return list.sort((a, b) => {
      if (col === 'progression') {
        const aHas = a.montant_alloue > 0;
        const bHas = b.montant_alloue > 0;
        if (aHas !== bHas) return aHas ? -1 : 1; // sans budget toujours en bas
        return (a.pourcentage_consomme - b.pourcentage_consomme) * dir;
      }
      if (col === 'categorie') {
        return (
          a.categorie_nom.localeCompare(b.categorie_nom, 'fr', { sensitivity: 'base' }) * dir
        );
      }
      if (col === 'budget') return (a.montant_alloue - b.montant_alloue) * dir;
      if (col === 'depense') return (a.montant_depense - b.montant_depense) * dir;
      if (col === 'restant') return (restantOf(a) - restantOf(b)) * dir;
      return 0;
    });
  }, [items, showEmpty, sort]);

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sort.column !== column || !sort.direction) {
      return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    }
    return sort.direction === 'asc' ? (
      <ArrowUp className="w-3 h-3 text-primary-400" />
    ) : (
      <ArrowDown className="w-3 h-3 text-primary-400" />
    );
  };

  const SortHeader: React.FC<{
    column: SortColumn;
    label: string;
    align?: 'left' | 'right';
  }> = ({ column, label, align = 'left' }) => (
    <button
      type="button"
      onClick={() => cycleSort(column)}
      className={`inline-flex items-center gap-1 hover:text-white transition ${
        align === 'right' ? 'justify-end w-full' : ''
      } ${sort.column === column ? 'text-white' : ''}`}
    >
      <span>{label}</span>
      <SortIcon column={column} />
    </button>
  );

  const stats = useMemo(() => {
    const budgetTotal = activeItems.reduce((s, i) => s + i.montant_alloue, 0);
    const depense = activeItems.reduce((s, i) => s + i.montant_depense, 0);
    const restant = budgetTotal - depense;
    const pctDepense = budgetTotal > 0 ? (depense / budgetTotal) * 100 : 0;
    const pctRestant = budgetTotal > 0 ? (restant / budgetTotal) * 100 : 0;
    const vsPrev =
      prevBudgetTotal > 0.01
        ? ((budgetTotal - prevBudgetTotal) / prevBudgetTotal) * 100
        : 0;
    return { budgetTotal, depense, restant, pctDepense, pctRestant, vsPrev };
  }, [activeItems, prevBudgetTotal]);

  const distribution = useMemo(() => {
    const total = stats.budgetTotal || 1;
    return [...activeItems]
      .sort((a, b) => b.montant_alloue - a.montant_alloue)
      .map((i) => ({
        ...i,
        pct: (i.montant_alloue / total) * 100,
      }));
  }, [activeItems, stats.budgetTotal]);

  const topSpent = useMemo(
    () => [...activeItems].sort((a, b) => b.montant_depense - a.montant_depense).slice(0, 5),
    [activeItems],
  );
  const topMax = topSpent[0]?.montant_depense || 1;

  const openEdit = (item: BudgetConsommation) => {
    setEditItem(item);
    setEditAmount(item.montant_alloue > 0 ? String(item.montant_alloue) : '');
  };

  const closeEdit = () => {
    setEditItem(null);
    setEditAmount('');
  };

  const handleSave = async () => {
    if (!editItem) return;

    const normalized = editAmount.replace(',', '.').trim();
    const montant = Number(normalized);
    if (!normalized || Number.isNaN(montant) || montant < 0) {
      alert('Veuillez saisir un montant positif ou nul.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await upsertBudget(editItem.categorie_id, moisDate, montant);
    setSaving(false);

    if (saveError) {
      alert("Impossible d'enregistrer le budget.");
      return;
    }

    closeEdit();
    loadBudgets();
  };

  if (accessLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!budgetsEnabled) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center space-y-3">
        <Lock className="w-12 h-12 text-dark-500" />
        <h2 className="text-xl font-semibold text-white">Budgets indisponibles</h2>
        <p className="text-dark-400 max-w-md">
          Cette fonctionnalité n&apos;est pas activée pour votre compte.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Budgets par catégorie</h1>
          <p className="text-dark-400 mt-2">
            Définissez un montant mensuel par catégorie et suivez votre consommation
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-dark-800 border border-dark-700 rounded-lg px-1">
            <button
              type="button"
              onClick={() => shiftMonth(-1)}
              className="p-2 text-white hover:bg-dark-700 rounded-md"
              aria-label="Mois précédent"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <select
              value={monthIndex}
              onChange={(e) => setMonthIndex(Number(e.target.value))}
              className="bg-transparent text-white text-sm focus:outline-none capitalize py-2"
            >
              {MONTHS.map((label, index) => (
                <option key={label} value={index} className="bg-dark-900">
                  {label}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="bg-transparent text-white text-sm focus:outline-none py-2"
            >
              {Array.from({ length: 7 }, (_, i) => year - 3 + i).map((y) => (
                <option key={y} value={y} className="bg-dark-900">
                  {y}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => shiftMonth(1)}
              className="p-2 text-white hover:bg-dark-700 rounded-md"
              aria-label="Mois suivant"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 bg-dark-800 border border-dark-700 hover:border-dark-500 text-white rounded-lg text-sm"
          >
            <Sparkles className="w-4 h-4 text-primary-400" />
            Configurer
          </button>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" />
            Nouvelle catégorie
          </Link>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
        </div>
      ) : error ? (
        <div className="text-center space-y-3">
          <p className="text-red-400">{error}</p>
          <button
            type="button"
            onClick={loadBudgets}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Réessayer
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <AnimatedCard className="bg-dark-800 border border-dark-700 rounded-xl p-4" delay={0}>
              <p className="text-dark-400 text-sm">Budget total</p>
              <p className="text-2xl font-bold text-white mt-1">
                <AnimatedNumber value={stats.budgetTotal} suffix=" €" />
              </p>
              <p className="text-xs text-dark-500 mt-1">Montant total alloué</p>
              <p
                className={`text-xs mt-2 ${
                  stats.vsPrev >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}
              >
                {stats.vsPrev >= 0 ? '+' : ''}
                {stats.vsPrev.toFixed(2)} % vs mois dernier
              </p>
            </AnimatedCard>
            <AnimatedCard className="bg-dark-800 border border-dark-700 rounded-xl p-4" delay={0.05}>
              <p className="text-dark-400 text-sm">Dépensé</p>
              <p className="text-2xl font-bold text-white mt-1">
                <AnimatedNumber value={stats.depense} suffix=" €" />
              </p>
              <p className="text-xs text-dark-400 mt-2">{stats.pctDepense.toFixed(1)} % du budget</p>
              <div className="h-1.5 bg-dark-700 rounded-full mt-2 overflow-hidden">
                <AnimatedProgressBar
                  percentage={stats.pctDepense}
                  className="h-full bg-emerald-500 rounded-full"
                />
              </div>
            </AnimatedCard>
            <AnimatedCard className="bg-dark-800 border border-dark-700 rounded-xl p-4" delay={0.1}>
              <p className="text-dark-400 text-sm">Restant</p>
              <p
                className={`text-2xl font-bold mt-1 ${
                  stats.restant >= 0 ? 'text-sky-400' : 'text-red-400'
                }`}
              >
                <AnimatedNumber value={stats.restant} suffix=" €" />
              </p>
              <p className="text-xs text-dark-400 mt-2">
                {Math.max(stats.pctRestant, 0).toFixed(1)} % du budget
              </p>
              <div className="h-1.5 bg-dark-700 rounded-full mt-2 overflow-hidden">
                <AnimatedProgressBar
                  percentage={Math.max(stats.pctRestant, 0)}
                  className="h-full bg-sky-500 rounded-full"
                />
              </div>
            </AnimatedCard>
            <AnimatedCard className="bg-dark-800 border border-dark-700 rounded-xl p-4" delay={0.15}>
              <p className="text-dark-400 text-sm">Catégories</p>
              <p className="text-2xl font-bold text-white mt-1">
                <AnimatedNumber value={activeItems.length} decimals={0} />
              </p>
              <p className="text-xs text-dark-500 mt-2">Actives ce mois-ci</p>
              <p className="text-xs text-dark-500 mt-1">
                {items.length - activeItems.length} sans budget défini
              </p>
            </AnimatedCard>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6">
            <div className="bg-dark-800 border border-dark-700 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-dark-700">
                <label className="inline-flex items-center gap-2 text-sm text-dark-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showEmpty}
                    onChange={toggleShowEmpty}
                    className="rounded border-dark-500 bg-dark-900 text-primary-500 focus:ring-primary-500"
                  />
                  Afficher les catégories sans dépenses
                </label>
                <span className="text-xs text-dark-500">
                  {displayedItems.length}/{items.length} affichée
                  {displayedItems.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="hidden md:grid grid-cols-[minmax(0,1.4fr)_100px_100px_100px_minmax(120px,1fr)_56px] gap-2 px-4 py-3 text-[11px] uppercase tracking-wide text-dark-400 border-b border-dark-700">
                <SortHeader column="categorie" label="Catégorie" />
                <SortHeader column="budget" label="Budget" align="right" />
                <SortHeader column="depense" label="Dépensé" align="right" />
                <SortHeader column="restant" label="Restant" align="right" />
                <SortHeader column="progression" label="Progression" />
                <span className="text-right">Action</span>
              </div>

              {displayedItems.length === 0 ? (
                <p className="text-dark-400 text-center py-12">
                  {items.length === 0
                    ? 'Aucune catégorie de dépense.'
                    : 'Aucune catégorie à afficher (activez « Afficher les catégories sans dépenses »).'}
                </p>
              ) : (
                <ul className="divide-y divide-dark-700">
                  {displayedItems.map((item, index) => {
                    const hasBudget = item.montant_alloue > 0;
                    const restant = item.montant_alloue - item.montant_depense;
                    const status = getBudgetProgressStatus(
                      item.montant_alloue,
                      item.pourcentage_consomme,
                      item.depassement,
                    );
                    const width = hasBudget
                      ? Math.min(Math.max(item.pourcentage_consomme, 0), 100)
                      : 0;
                    return (
                      <motion.li
                        key={item.categorie_id}
                        className="grid grid-cols-1 md:grid-cols-[minmax(0,1.4fr)_100px_100px_100px_minmax(120px,1fr)_56px] gap-2 px-4 py-3 items-center hover:bg-dark-700/30"
                        custom={index}
                        variants={prefersReducedMotion ? undefined : rowVariants}
                        initial={prefersReducedMotion ? false : 'hidden'}
                        animate={prefersReducedMotion ? undefined : 'visible'}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <CategoryIconBadge
                            color={item.categorie_couleur}
                            icon={item.categorie_icone}
                            size={32}
                          />
                          <span className="text-white font-medium truncate">
                            {item.categorie_nom}
                          </span>
                        </div>
                        <p className="md:text-right text-dark-200 text-sm">
                          {hasBudget ? formatEuro(item.montant_alloue) : '—'}
                        </p>
                        <p className="md:text-right text-white text-sm font-medium">
                          {formatEuro(item.montant_depense)}
                        </p>
                        <p
                          className={`md:text-right text-sm ${
                            !hasBudget
                              ? 'text-dark-500'
                              : restant >= 0
                                ? 'text-emerald-400'
                                : 'text-red-400'
                          }`}
                        >
                          {hasBudget ? formatEuro(restant) : '—'}
                        </p>
                        <div>
                          {hasBudget ? (
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-dark-700 rounded-full overflow-hidden">
                                <AnimatedProgressBar
                                  percentage={width}
                                  className={`h-full rounded-full ${getBudgetProgressClass(status)}`}
                                />
                              </div>
                              <span className="text-[11px] text-dark-400 w-10 text-right">
                                {item.pourcentage_consomme.toFixed(0)} %
                              </span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openEdit(item)}
                              className="text-xs text-dark-500 italic hover:text-primary-400"
                            >
                              Définir un budget
                            </button>
                          )}
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => openEdit(item)}
                            className="p-2 text-dark-400 hover:text-white hover:bg-dark-600 rounded-lg"
                            title="Modifier"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.li>
                    );
                  })}
                </ul>
              )}
              <p className="px-4 py-3 text-xs text-dark-500 border-t border-dark-700">
                Astuce : vous pouvez ajuster vos budgets en cours de mois selon vos besoins.
              </p>
            </div>

            <aside className="space-y-4">
              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-4">Répartition du budget</h3>
                {distribution.length === 0 ? (
                  <p className="text-sm text-dark-400">Aucun budget actif</p>
                ) : (
                  <>
                    <div
                      className="mx-auto w-36 h-36 rounded-full mb-4"
                      style={{
                        background: `conic-gradient(${distribution
                          .map((d, i) => {
                            const start = distribution
                              .slice(0, i)
                              .reduce((s, x) => s + x.pct, 0);
                            return `${d.categorie_couleur || '#60A5FA'} ${start}% ${start + d.pct}%`;
                          })
                          .join(', ')})`,
                        maskImage: 'radial-gradient(transparent 52%, black 54%)',
                        WebkitMaskImage: 'radial-gradient(transparent 52%, black 54%)',
                      }}
                    />
                    <ul className="space-y-2">
                      {distribution.slice(0, 6).map((d) => (
                        <li
                          key={d.categorie_id}
                          className="flex items-center justify-between gap-2 text-sm"
                        >
                          <span className="flex items-center gap-2 text-dark-300 min-w-0">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: d.categorie_couleur || '#60A5FA' }}
                            />
                            <span className="truncate">{d.categorie_nom}</span>
                          </span>
                          <span className="text-dark-400 shrink-0">{d.pct.toFixed(0)} %</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-4">Top catégories</h3>
                {topSpent.length === 0 ? (
                  <p className="text-sm text-dark-400">Pas encore de dépenses</p>
                ) : (
                  <ul className="space-y-3">
                    {topSpent.map((item) => (
                      <li key={item.categorie_id}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-dark-300 truncate">{item.categorie_nom}</span>
                          <span className="text-white shrink-0">
                            {formatEuro(item.montant_depense)}
                          </span>
                        </div>
                        <div className="h-1.5 bg-dark-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${(item.montant_depense / topMax) * 100}%`,
                              backgroundColor: item.categorie_couleur || '#34D399',
                            }}
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 flex gap-3">
                <Lightbulb className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold text-sm mb-1">Astuce</h3>
                  <p className="text-xs text-dark-400 leading-relaxed">
                    Basez vos plafonds sur vos 3 derniers mois via « Configurer », avec une marge
                    de −10&nbsp;% pour progresser sans frustration.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </>
      )}

      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-dark-900 border border-dark-800 rounded-xl p-6 w-full max-w-md space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-white">
                Budget — {editItem.categorie_nom}
              </h3>
              <p className="text-dark-400 text-sm capitalize mt-1">
                {monthNameFromIndex(monthIndex)} {year}
              </p>
            </div>
            <input
              type="text"
              inputMode="decimal"
              value={editAmount}
              onChange={(e) => setEditAmount(e.target.value)}
              placeholder="Montant alloué (€)"
              className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
            />
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeEdit}
                className="px-4 py-2 text-dark-300 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BudgetSetupWizard
        isOpen={wizardOpen}
        mois={moisDate}
        moisLabel={`${monthNameFromIndex(monthIndex)} ${year}`}
        onClose={() => setWizardOpen(false)}
        onSaved={loadBudgets}
      />
    </div>
  );
};

export default Budgets;
